import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useSettings } from "../context/SettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Save } from "lucide-react";

export default function Settings() {
  const { t } = useLang();
  const { logoUrl, refresh } = useSettings();
  const fileRef = useRef();
  const [shops, setShops] = useState([]);
  const [newShop, setNewShop] = useState("");
  const [legal, setLegal] = useState({ impressum_de: "", impressum_en: "", datenschutz_de: "", datenschutz_en: "" });
  const [savingLegal, setSavingLegal] = useState(false);

  const loadShops = async () => {
    try {
      const { data } = await api.get("/shop-origins");
      setShops(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadLegal = async () => {
    try {
      const { data } = await api.get("/legal");
      setLegal({
        impressum_de: data.impressum_de || "",
        impressum_en: data.impressum_en || "",
        datenschutz_de: data.datenschutz_de || "",
        datenschutz_en: data.datenschutz_en || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadShops(); loadLegal(); }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/settings/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("common.saved"));
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/settings/logo");
      toast.success(t("common.saved"));
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addShop = async () => {
    if (!newShop.trim()) return;
    try {
      await api.post("/shop-origins", { name: newShop.trim() });
      setNewShop("");
      loadShops();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteShop = async (id) => {
    try {
      await api.delete(`/shop-origins/${id}`);
      loadShops();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveLegal = async () => {
    setSavingLegal(true);
    try {
      await api.put("/legal", legal);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingLegal(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-8">
      <div>
        <p className="label-mini">Admin</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-none mt-1">{t("settings.title")}</h1>
      </div>

      {/* Logo */}
      <div className="swiss-card p-6">
        <h2 className="text-xl font-bold mb-1">{t("settings.logo")}</h2>
        <p className="label-mini mb-4">{t("settings.logoCurrent")}</p>

        <div className="flex items-center gap-4 mb-4 p-4 bg-[#F8F9FA] border border-border" style={{ borderRadius: 4 }}>
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-14 w-auto object-contain" data-testid="settings-current-logo" />
          ) : (
            <div className="h-14 px-4 flex items-center justify-center text-sm text-muted-foreground" data-testid="settings-no-logo">
              Kein Logo hochgeladen
            </div>
          )}
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/svg+xml,image/jpeg"
          className="hidden"
          onChange={(e) => uploadLogo(e.target.files?.[0])}
          data-testid="settings-logo-input"
        />
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={() => fileRef.current?.click()} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" style={{ borderRadius: 4 }} data-testid="settings-logo-upload">
            <Upload className="w-4 h-4 mr-2" /> {t("settings.uploadLogo")}
          </Button>
          {logoUrl && (
            <Button onClick={removeLogo} variant="outline" className="border-destructive text-destructive" style={{ borderRadius: 4 }} data-testid="settings-logo-remove">
              <Trash2 className="w-4 h-4 mr-2" /> {t("settings.removeLogo")}
            </Button>
          )}
        </div>
      </div>

      {/* Shop Origins */}
      <div className="swiss-card p-6">
        <h2 className="text-xl font-bold mb-4">{t("settings.shopOrigins")}</h2>
        <div className="flex gap-2 mb-4">
          <Input
            value={newShop}
            onChange={(e) => setNewShop(e.target.value)}
            placeholder={t("settings.shopName")}
            data-testid="settings-shop-input"
            style={{ borderRadius: 4 }}
          />
          <Button onClick={addShop} className="bg-[#0055FF] hover:bg-[#0044CC] text-white shrink-0" style={{ borderRadius: 4 }} data-testid="settings-shop-add">
            <Plus className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">{t("settings.addShop")}</span>
          </Button>
        </div>
        <ul className="divide-y divide-border border border-border" style={{ borderRadius: 4 }} data-testid="settings-shop-list">
          {shops.map((s) => (
            <li key={s.id} className="flex items-center justify-between px-3 py-2" data-testid={`shop-item-${s.name}`}>
              <span className="font-medium">{s.name}</span>
              <button
                onClick={() => deleteShop(s.id)}
                className="text-muted-foreground hover:text-destructive p-1"
                aria-label="Löschen"
                data-testid={`shop-delete-${s.name}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
          {shops.length === 0 && (
            <li className="px-3 py-4 text-center text-muted-foreground text-sm">—</li>
          )}
        </ul>
      </div>

      {/* Legal content editor */}
      <div className="swiss-card p-6">
        <h2 className="text-xl font-bold mb-1">{t("settings.legal") || "Rechtliche Inhalte"}</h2>
        <p className="label-mini mb-4">Impressum & Datenschutzerklärung</p>

        <Tabs defaultValue="impressum" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4" style={{ borderRadius: 4 }}>
            <TabsTrigger value="impressum" data-testid="legal-tab-impressum">Impressum</TabsTrigger>
            <TabsTrigger value="datenschutz" data-testid="legal-tab-datenschutz">Datenschutz</TabsTrigger>
          </TabsList>

          <TabsContent value="impressum" className="space-y-4">
            <LegalField
              label="Deutsch"
              testid="legal-impressum-de"
              value={legal.impressum_de}
              onChange={(v) => setLegal((l) => ({ ...l, impressum_de: v }))}
            />
            <LegalField
              label="English"
              testid="legal-impressum-en"
              value={legal.impressum_en}
              onChange={(v) => setLegal((l) => ({ ...l, impressum_en: v }))}
            />
          </TabsContent>

          <TabsContent value="datenschutz" className="space-y-4">
            <LegalField
              label="Deutsch"
              testid="legal-datenschutz-de"
              value={legal.datenschutz_de}
              onChange={(v) => setLegal((l) => ({ ...l, datenschutz_de: v }))}
            />
            <LegalField
              label="English"
              testid="legal-datenschutz-en"
              value={legal.datenschutz_en}
              onChange={(v) => setLegal((l) => ({ ...l, datenschutz_en: v }))}
            />
          </TabsContent>
        </Tabs>

        <p className="text-xs text-muted-foreground mt-4">
          Hinweis: Lassen Sie ein Feld leer, um den Standard-Platzhaltertext anzuzeigen. Markdown-Syntax wird unterstützt (Überschriften ##, Absätze, leere Zeilen).
        </p>

        <Button
          onClick={saveLegal}
          disabled={savingLegal}
          className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] text-white"
          style={{ borderRadius: 4 }}
          data-testid="legal-save-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {savingLegal ? t("common.loading") : t("profile.save")}
        </Button>
      </div>
    </div>
  );
}

function LegalField({ label, testid, value, onChange }) {
  return (
    <div>
      <label className="label-mini mb-1.5 block">{label}</label>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={10}
        className="font-mono text-xs"
        style={{ borderRadius: 4 }}
        data-testid={testid}
        placeholder="## Anbieter&#10;Beta-Webagentur, Düsseldorf&#10;&#10;## Kontakt&#10;..."
      />
    </div>
  );
}
